import React from "react";
import { Box, Text, Button, Flex } from "@chakra-ui/react";
import { CustomButton } from "@root/components/common/button/customButton";
import { useTranslation } from "react-i18next";
import { CalenderIcon } from "@root/assets/icons";
import { SavedIcon } from "@root/assets/icons/SavedIcon";
import { FillSavedIcon } from "@root/assets/icons/FillSavedIcon";
import { formatDateToISO, formatYear } from "@root/utils/date-helper";
import { REQUIREMENT_TYPE } from "@root/constants";

interface CourseHeaderProps {
  courseData: any;
  enrolled: boolean;
  addCourseEnrollLoading: boolean;
  watchLaterLoading: boolean;
  isCourseExpired: boolean;
  onEnroll: () => void;
  onWatchLater: () => void;
  onUndoWatchLater: () => void;
  onUpdateWatchLater: () => void;
}

const CourseHeader: React.FC<CourseHeaderProps> = ({
  courseData,
  enrolled,
  addCourseEnrollLoading,
  watchLaterLoading,
  isCourseExpired,
  onEnroll,
  onWatchLater,
  onUndoWatchLater,
  onUpdateWatchLater,
}) => {
  const { t } = useTranslation("common");

  const requirementLabel =
    courseData?.requirement === REQUIREMENT_TYPE.REQUIRED
      ? t("coursesPage.essentialRequired")
      : courseData?.requirement === REQUIREMENT_TYPE.OPTIONAL
        ? t("coursesPage.optionalRecommended")
        : null;

  // Determine if enroll button should be disabled
  const isEnrollDisabled = enrolled || isCourseExpired;

  return (
    <Box mb={6}>
      <Text fontWeight={700} fontSize="2xl" mb={1}>
        {courseData?.maintitle || courseData?.title}
      </Text>

      <Text color="gray.500" fontSize="sm" mb={4}>
        {courseData?.desc || courseData?.description}
      </Text>

      <Flex align="center" justify="space-between" flexWrap="wrap" mb={2}>
        <Flex align="center" gap={3}>
          <Text fontWeight={600} fontSize="14px" color={"#5C6670"}>
            {formatYear(courseData?.createdAt)}
          </Text>
          <Box w={2} h={2} borderRadius="full" bg="#009B9B" />
          <Text
            color="#0073E0"
            fontWeight={600}
            fontSize="14px"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
          >
            {requirementLabel}
          </Text>
        </Flex>

        <Flex gap={3} alignItems="center">
          <Flex
            gap={3}
            alignItems="center"
            data-tour-item="journalist-cd-watch-later"
          >
            {/* Watch Later functionality - show for both enrolled and non-enrolled */}
            {courseData?.isCourseTaken?.isWatchLater === true && (
              <Flex
                border={"1px solid"}
                w={"282px"}
                borderColor={"#CDD6DC"}
                borderRadius="82px"
                py={2}
                px={4}
                justifyContent={"space-between"}
                boxShadow={"0px 4px 4px 0px #2023261A"}
                alignItems="center"
                fontWeight={500}
                fontSize="14px"
                color={"#2F2F34"}
                gap={2}
              >
                {t("courseDetail.savedToWatchLater")}
                <Button
                  variant="link"
                  color={"#1819B4"}
                  fontWeight={500}
                  fontSize="14px"
                  ml={2}
                  p={0}
                  h="auto"
                  width={"fit-content"}
                  isLoading={watchLaterLoading}
                  isDisabled={watchLaterLoading}
                  onClick={onUndoWatchLater}
                >
                  {t("courseDetail.undo")} ↩
                </Button>
              </Flex>
            )}
            {/* {(!courseData?.isCourseTaken ||
              courseData?.isCourseTaken?.isWatchLater === false) && (
              <CustomButton
                leftIcon={<SavedIcon />}
                text={t("courseDetail.watchLater")}
                color="#1819B4"
                fontWeight={500}
                fontSize="14px"
                borderRadius={"8px"}
                borderColor={"#1819B4"}
                border={"1px solid"}
                width="fit-content"
                height={"36px"}
                textColor="#1819B4"
                background="transparent"
                _hover={{ background: "transparent" }}
                isLoading={watchLaterLoading}
                onClick={enrolled ? onUpdateWatchLater : onWatchLater}
              />
            )} */}

            {courseData?.isCourseTaken?.isWatchLater === true && (
              <CustomButton
                leftIcon={<FillSavedIcon />}
                text={t("courseDetail.saved")}
                color="#1819B4"
                fontWeight={500}
                fontSize="14px"
                borderRadius={"8px"}
                borderColor={"#1819B4"}
                border={"1px solid"}
                width="fit-content"
                height={"36px"}
                textColor="#1819B4"
                background="transparent"
                _hover={{ background: "transparent" }}
                isLoading={watchLaterLoading}
                onClick={onUndoWatchLater}
              />
            )}
          </Flex>

          {/* Enroll button - disabled when enrolled OR when course is expired */}
          <Box data-tour-item="journalist-cd-enroll">
            <CustomButton
              text={
                enrolled
                  ? t("courseDetail.enrolled")
                  : t("courseDetail.enrollNow")
              }
              colorScheme="purple"
              fontWeight={500}
              fontSize="14px"
              borderRadius={"8px"}
              width="fit-content"
              height={"36px"}
              background={isEnrollDisabled ? "#A0A0A0" : "#1819B4"}
              _hover={{ background: isEnrollDisabled ? "#A0A0A0" : "#1819B4" }}
              _disabled={{
                background: "#A0A0A0",
                cursor: "not-allowed",
                opacity: 0.6,
              }}
              isLoading={addCourseEnrollLoading}
              onClick={onEnroll}
              isDisabled={isEnrollDisabled}
            />
          </Box>
        </Flex>
      </Flex>

      {/* Enrollment expired warning message */}
      {isCourseExpired && !enrolled && (
        <Text color="#E53E3E" fontSize="14px" fontWeight={500} mt={2}>
          {t("courseDetail.enrollmentDeadlineExpired")}
        </Text>
      )}

      <Flex
        align="center"
        color="gray.500"
        fontSize="sm"
        mt={2}
        gap={2}
        data-tour-item="journalist-cd-enrollment-period"
      >
        <CalenderIcon />
        <Text
          display={"flex"}
          alignItems={"center"}
          gap={2}
          fontSize={"14px"}
          fontWeight={400}
          color={"#5C6670"}
        >
          {t("courseDetail.enrollmentPeriod")}:{" "}
          <Text fontWeight={500} fontSize={"14px"} color={"#5C6670"}>
            {courseData?.enrollmentPeriodStart &&
              formatDateToISO(
                new Date(courseData?.enrollmentPeriodStart),
              )?.replace(/-/g, ".")}{" "}
            ~{" "}
            {courseData?.enrollmentPeriodEnd &&
              formatDateToISO(
                new Date(courseData?.enrollmentPeriodEnd),
              )?.replace(/-/g, ".")}
          </Text>
        </Text>
      </Flex>
    </Box>
  );
};

export default CourseHeader;
